import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useProjects } from '../hooks/useProjects';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ScrollArea } from '../components/ui/scroll-area';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, List, Download,
  Trash2, Loader2, Copy, Search, FileText, Info as InfoIcon, Pencil,
  FolderOpen, FolderSync, Check, X as XIcon, RefreshCw, PackageCheck, Send, History
} from 'lucide-react';
import axios from 'axios';
import { getDirectoryHandle, saveDirectoryHandle, removeDirectoryHandle, walkDirectory } from '../utils/workingDirectory';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Tooltip, TooltipContent, TooltipProvider, TooltipTrigger,
} from '../components/ui/tooltip';
import { SECURITY_CLASSIFICATIONS as PROJECT_SECURITY_CLASSIFICATIONS } from '../constants/projectOptions';
import { ALL_SNS_CODES } from '../data/snsData';
import { INFO_CODES } from '../data/infoCodesData';
import { getSubSystemsForSystem, getSubSubSystemsForSubSystem } from '../data/snsHierarchy';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// S1000D Data Module Schema types
const DM_SCHEMA_TYPES = [
  "Applicability Cross Reference (ACT)",
  "BRDocs",
  "BREX",
  "Checklist",
  "Common Repository",
  "Conditions Cross Reference (CCT)",
  "Crew",
  "Descriptive",
  "Fault",
  "Illustrated Parts Data (IPD)",
  "Learning",
  "Procedural",
  "Process",
  "Product Cross Reference (PCT)",
  "Schedule",
  "Service Bulletin",
  "Wiring Data",
  "Wiring Fields",
];

// Parse project security classifications to value/label pairs
const SECURITY_CLASSIFICATIONS = PROJECT_SECURITY_CLASSIFICATIONS.map(s => {
  const code = s.split(' - ')[0].trim();
  return { value: s, label: s, code };
});

const ITEM_LOCATION_CODES = [
  { value: "A", label: "A - Fitted to installed equipment" },
  { value: "B", label: "B - Spare equipment (external)" },
  { value: "C", label: "C - All" },
  { value: "D", label: "D - All (general)" },
  { value: "T", label: "T - Training" },
];

const DML_TYPES = [
  { value: "S", label: "S - Status" },
  { value: "P", label: "P - Preliminary" },
  { value: "C", label: "C - Changed" },
];

// (Sub-system codes are now dynamically loaded from snsHierarchy.js)

// A-Z variant codes
const VARIANT_CODES = Array.from({ length: 26 }, (_, i) => {
  const c = String.fromCharCode(65 + i);
  return { value: c, label: c };
});

const ISSUE_NUMBERS = Array.from({ length: 999 }, (_, i) => {
  const num = String(i + 1).padStart(3, '0');
  return { value: num, label: num };
});

const IN_WORK_OPTIONS = Array.from({ length: 100 }, (_, i) => {
  const num = String(i).padStart(2, '0');
  return { value: num, label: num };
});

const currentYear = new Date().getFullYear();
const YEAR_OPTIONS = Array.from({ length: 11 }, (_, i) => {
  const y = String(currentYear - 5 + i);
  return { value: y, label: y };
});

// Info tooltip helper
const FieldLabel = ({ label, tooltip, testId }) => (
  <div className="flex items-center gap-1.5 mb-1">
    <Label className="text-sm font-semibold text-slate-800">{label}</Label>
    {tooltip && (
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <InfoIcon className="w-4 h-4 text-blue-500 cursor-help shrink-0" data-testid={testId ? `${testId}-info` : undefined} />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-xs">
            <p>{tooltip}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    )}
  </div>
);

// Searchable Select for large dropdowns
const SearchableSelect = ({ options, value, onChange, placeholder, label, testId }) => {
  const [search, setSearch] = useState('');
  const [isOpen, setIsOpen] = useState(false);

  const filtered = useMemo(() => {
    if (!search) return options;
    const q = search.toLowerCase();
    return options.filter(o => `${o.code} - ${o.name}`.toLowerCase().includes(q));
  }, [search, options]);

  const selectedOption = options.find(o => o.code === value);

  return (
    <div className="relative">
      <div
        className="flex items-center w-full rounded-md border border-input bg-background px-3 py-2 text-sm cursor-pointer hover:border-violet-400 transition-colors"
        onClick={() => setIsOpen(!isOpen)}
        data-testid={testId}
      >
        <span className={`flex-1 truncate font-mono ${value ? 'text-foreground' : 'text-muted-foreground'}`}>
          {selectedOption ? `${selectedOption.code} - ${selectedOption.name}` : placeholder}
        </span>
        <Search className="w-3.5 h-3.5 text-muted-foreground ml-2 shrink-0" />
      </div>
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg">
          <div className="p-2 border-b">
            <Input value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder={`Search ${label || ''}...`} className="h-8 text-sm" autoFocus
              onClick={(e) => e.stopPropagation()} />
          </div>
          <ScrollArea className="h-64">
            <div className="p-1">
              {filtered.length === 0 ? (
                <p className="text-sm text-slate-400 px-2 py-3 text-center">No results found</p>
              ) : filtered.map((o) => (
                <div key={o.code}
                  className={`px-2 py-1.5 text-sm rounded cursor-pointer hover:bg-violet-50 transition-colors font-mono ${value === o.code ? 'bg-violet-100 text-violet-700' : ''}`}
                  onClick={(e) => { e.stopPropagation(); onChange(o.code); setIsOpen(false); setSearch(''); }}>
                  {o.code} - {o.name}
                </div>
              ))}
            </div>
          </ScrollArea>
        </div>
      )}
      {isOpen && <div className="fixed inset-0 z-40" onClick={() => { setIsOpen(false); setSearch(''); }} />}
    </div>
  );
};

const DMLBuilderPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { token, user } = useAuth();
  const isAdmin = user?.role === 'admin';

  // Log tool usage
  useEffect(() => {
    if (token) {
      axios.post(`${API}/history/tool-usage`, { tool_name: 'DML Builder' }, { headers: { Authorization: `Bearer ${token}` } }).catch(() => {});
    }
  }, []);

  const initialProject = location.state?.project;
  const initialProjectId = location.state?.projectId;

  const [activeTab, setActiveTab] = useState('info');
  const [isLoading, setIsLoading] = useState(false);
  const [selectedDml, setSelectedDml] = useState(null);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const [showDuplicateAlert, setShowDuplicateAlert] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null); // index of entry being edited
  const [exportContent, setExportContent] = useState('');
  const [exportFilename, setExportFilename] = useState('');

  // Working directory state (File System Access API)
  const [dirHandle, setDirHandle] = useState(null);
  const [dirFiles, setDirFiles] = useState([]); // [{name, handle, size, lastModified}]
  const [dirIcns, setDirIcns] = useState([]); // ICN graphic files [{name, handle}]
  const [dirName, setDirName] = useState('');
  const [scanningDir, setScanningDir] = useState(false);

  // Project selection
  const projects = useProjects();
  const [selectedProject, setSelectedProject] = useState(initialProjectId || 'none');
  const [currentProjectName, setCurrentProjectName] = useState(initialProject?.title || null);
  const [currentProject, setCurrentProject] = useState(initialProject);
  const [currentProjectId, setCurrentProjectId] = useState(initialProjectId);

  // DMC Entry form
  const [dmcForm, setDmcForm] = useState({
    model_ident_code: '', system_diff_code: 'A',
    system_code: '00', sub_system_code: '0', sub_sub_system_code: '0',
    assy_code: '00', disassy_code: '00', disassy_code_variant: 'A',
    info_code: '000', info_code_variant: 'A', item_location_code: 'A',
    learn_code: '', learn_event_code: '',
    title: '', issue_number: '001', in_work: '00',
    security_classification: '',
    schema: '',
    responsible_partner_company: '', originator: '',
  });

  // DML Information form
  const [dmlInfoForm, setDmlInfoForm] = useState({
    model_ident_code: '', sender: '', sender_cage: '',
    dml_type: '', year_of_issue: String(currentYear),
    sequence_number: '00001', issue_number: '',
    in_work: '', security_classification: '01 - Unclassified',
  });

  // Publication Module form
  const [pubForm, setPubForm] = useState({
    model_ident_code: '', system_diff_code: 'A',
    pm_issuer: '', pm_number: '00001', pm_volume: '00',
    title: '', issue_number: '001', in_work: '00',
    security_classification: '',
  });

  // DDN form
  const [ddnForm, setDdnForm] = useState({
    model_ident_code: '', sender: '', sender_cage: '',
    ddn_number: '00001', receiver: '', receiver_cage: '',
    year_of_issue: String(currentYear),
    title: '', security_classification: '',
  });

  // DDN persistence & builder state
  const [savedDdn, setSavedDdn] = useState(null); // Persisted DDN from DB
  const [showDdnBuilder, setShowDdnBuilder] = useState(false);
  const [ddnDeliveryFiles, setDdnDeliveryFiles] = useState([]); // Selected delivery filenames
  const [ddnBuilding, setDdnBuilding] = useState(false);
  const [ddnZipBuilding, setDdnZipBuilding] = useState(false);
  const [showDdnHistory, setShowDdnHistory] = useState(false);
  const [ddnHistory, setDdnHistory] = useState([]);
  const [ddnHistoryLoading, setDdnHistoryLoading] = useState(false);
  const [ddnExportContent, setDdnExportContent] = useState('');
  const [ddnExportFilename, setDdnExportFilename] = useState('');
  const [showDdnExport, setShowDdnExport] = useState(false);
  // DDN dispatch addresses
  const [ddnDispatchTo, setDdnDispatchTo] = useState({ enterprise_name: '', street: '', postal_zip_code: '', city: '', country: '' });
  const [ddnDispatchFrom, setDdnDispatchFrom] = useState({ enterprise_name: '', street: '', postal_zip_code: '', city: '', country: '' });

  // Cascading dropdown: filtered sub-system options based on selected system
  const subSystemOptions = useMemo(() => {
    return getSubSystemsForSystem(dmcForm.system_code);
  }, [dmcForm.system_code]);

  // Cascading dropdown: filtered sub-sub-system options based on selected system + sub-system
  const subSubSystemOptions = useMemo(() => {
    return getSubSubSystemsForSubSystem(dmcForm.system_code, dmcForm.sub_system_code);
  }, [dmcForm.system_code, dmcForm.sub_system_code]);

  useEffect(() => {
    if (currentProjectId) { fetchOrCreateDml(); }
    else { setSelectedDml(null); }
  }, [currentProjectId, token]);

  useEffect(() => {
    if (currentProject) {
      setDmcForm(prev => ({
        ...prev,
        model_ident_code: currentProject.model_ident_codes?.[0] || prev.model_ident_code,
        system_diff_code: currentProject.system_difference_code || prev.system_diff_code,
        responsible_partner_company: currentProject.responsible_partner_companies?.[0]?.company_name || '',
        originator: currentProject.originators?.[0]?.company_name || '',
        security_classification: currentProject.security_classification || '01 - Unclassified',
      }));
    }
  }, [currentProject]);

  useEffect(() => {
    if (selectedDml) {
      const projectSecClass = currentProject?.security_classification || '01 - Unclassified';
      setDmlInfoForm({
        model_ident_code: selectedDml.model_ident_code || '',
        sender: selectedDml.sender || '',
        sender_cage: selectedDml.sender_cage || '',
        dml_type: selectedDml.dml_type || '',
        year_of_issue: selectedDml.year_of_issue || String(currentYear),
        sequence_number: selectedDml.sequence_number || '00001',
        issue_number: selectedDml.issue_number || '',
        in_work: selectedDml.in_work || '',
        security_classification: selectedDml.security_classification || projectSecClass,
      });
    }
  }, [selectedDml, currentProject]);

  const fetchOrCreateDml = async () => {
    try {
      const response = await axios.get(`${API}/dml?project_id=${currentProjectId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.length > 0) {
        // Use existing DML
        const detail = await axios.get(`${API}/dml/${response.data[0].id}`, { headers: { Authorization: `Bearer ${token}` } });
        setSelectedDml(detail.data);
      } else {
        // Auto-create a DML for this project
        const mic = currentProject?.model_ident_codes?.[0] || 'PROJ';
        const projectSecClass = currentProject?.security_classification || '01 - Unclassified';
        const createResp = await axios.post(`${API}/dml`, {
          project_id: currentProjectId,
          dml_code: `DML-${mic}-${currentProjectId.substring(0, 5).toUpperCase()}`,
          title: `${currentProject?.title || 'Project'} DML`,
          model_ident_code: mic,
          dml_type: 'S',
          issue_number: '001',
          in_work: '00',
          security_classification: projectSecClass,
          year_of_issue: String(currentYear),
          sequence_number: '00001',
          sender: '',
          sender_cage: '',
          entries: [],
        }, { headers: { Authorization: `Bearer ${token}` } });
        const detail = await axios.get(`${API}/dml/${createResp.data.id}`, { headers: { Authorization: `Bearer ${token}` } });
        setSelectedDml(detail.data);
      }
    } catch (error) { console.error('Failed to fetch/create DML'); }
  };

  const addEntry = async () => {
    if (!selectedDml) { toast.error('No DML available. Select a project first.'); return; }
    if (!dmcForm.model_ident_code || !dmcForm.title) { toast.error('Model Ident Code and Title are required'); return; }
    
    // Check for duplicate DMC (skip the entry being edited)
    const newDmc = formatDmcPreview();
    const existingDmcs = (selectedDml.entries || [])
      .filter((_, i) => i !== editingIndex)
      .map(e => e.dmc_formatted);
    if (existingDmcs.includes(newDmc)) {
      setShowDuplicateAlert(true);
      return;
    }
    
    setIsLoading(true);
    try {
      // If editing, remove the old entry first
      if (editingIndex !== null) {
        await axios.delete(`${API}/dml/${selectedDml.id}/entry/${editingIndex}`, { headers: { Authorization: `Bearer ${token}` } });
      }
      
      await axios.post(`${API}/dml/${selectedDml.id}/entry`, {
        dmc: {
          model_ident_code: dmcForm.model_ident_code.toUpperCase(),
          system_diff_code: dmcForm.system_diff_code.toUpperCase(),
          system_code: dmcForm.system_code,
          sub_system_code: dmcForm.sub_system_code,
          sub_sub_system_code: dmcForm.sub_sub_system_code,
          assy_code: dmcForm.assy_code,
          disassy_code: dmcForm.disassy_code,
          disassy_code_variant: dmcForm.disassy_code_variant.toUpperCase(),
          info_code: dmcForm.info_code,
          info_code_variant: dmcForm.info_code_variant.toUpperCase(),
          item_location_code: dmcForm.item_location_code.toUpperCase(),
          learn_code: dmcForm.learn_code, learn_event_code: dmcForm.learn_event_code,
        },
        title: dmcForm.title, issue_number: dmcForm.issue_number,
        in_work: dmcForm.in_work, security_classification: dmcForm.security_classification,
        schema: dmcForm.schema,
        responsible_partner_company: dmcForm.responsible_partner_company === '__none__' ? '' : dmcForm.responsible_partner_company,
        originator: dmcForm.originator === '__none__' ? '' : dmcForm.originator,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(editingIndex !== null ? 'Data module updated' : 'Data module added');
      setEditingIndex(null);
      refreshDml();
      setDmcForm(prev => ({ ...prev, title: '' }));
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add entry'); }
    finally { setIsLoading(false); }
  };

  const removeEntry = async (index) => {
    if (!selectedDml) return;
    try {
      await axios.delete(`${API}/dml/${selectedDml.id}/entry/${index}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Entry removed');
      refreshDml();
    } catch (error) { toast.error('Failed to remove entry'); }
  };

  const editEntry = (index) => {
    const entry = selectedDml?.entries?.[index];
    if (!entry) return;
    const dmc = entry.dmc || {};
    setDmcForm({
      model_ident_code: dmc.model_ident_code || '',
      system_diff_code: dmc.system_diff_code || 'A',
      system_code: dmc.system_code || '00',
      sub_system_code: dmc.sub_system_code || '0',
      sub_sub_system_code: dmc.sub_sub_system_code || '0',
      assy_code: dmc.assy_code || '00',
      disassy_code: dmc.disassy_code || '00',
      disassy_code_variant: dmc.disassy_code_variant || 'A',
      info_code: dmc.info_code || '000',
      info_code_variant: dmc.info_code_variant || 'A',
      item_location_code: dmc.item_location_code || 'A',
      learn_code: dmc.learn_code || '',
      learn_event_code: dmc.learn_event_code || '',
      title: entry.title || '',
      issue_number: entry.issue_number || '001',
      in_work: entry.in_work || '00',
      security_classification: entry.security_classification || currentProject?.security_classification || '01 - Unclassified',
      schema: entry.schema || '',
      responsible_partner_company: entry.responsible_partner_company || '',
      originator: entry.originator || '',
    });
    setEditingIndex(index);
    setActiveTab('add');
  };

  const cancelEdit = () => {
    setEditingIndex(null);
    setDmcForm(prev => ({ ...prev, title: '' }));
  };

  const addPubModule = async () => {
    if (!selectedDml) { toast.error('No DML available. Select a project first.'); return; }
    if (!pubForm.model_ident_code || !pubForm.title) { toast.error('Model Ident Code and Title are required'); return; }
    setIsLoading(true);
    try {
      const pmCode = `PMC-${pubForm.model_ident_code}-${pubForm.pm_issuer || 'XX'}-${pubForm.pm_number}-${pubForm.pm_volume}`;
      await axios.post(`${API}/dml/${selectedDml.id}/entry`, {
        dmc: {
          model_ident_code: pubForm.model_ident_code.toUpperCase(),
          system_diff_code: pubForm.system_diff_code.toUpperCase(),
          system_code: '00', sub_system_code: '0', sub_sub_system_code: '0',
          assy_code: '00', disassy_code: '00', disassy_code_variant: 'A',
          info_code: '00S', info_code_variant: 'A', item_location_code: 'D',
          learn_code: '', learn_event_code: '',
        },
        title: pubForm.title, issue_number: pubForm.issue_number,
        in_work: pubForm.in_work, security_classification: pubForm.security_classification,
        schema: 'Publication Module',
        responsible_partner_company: '', originator: '',
        entry_type: 'pm', pm_code: pmCode,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Publication Module added');
      refreshDml();
      setPubForm(prev => ({ ...prev, title: '' }));
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add publication module'); }
    finally { setIsLoading(false); }
  };

  const addDDN = async () => {
    if (!selectedDml) { toast.error('No DML available. Select a project first.'); return; }
    if (!ddnForm.model_ident_code || !ddnForm.title) { toast.error('Model Ident Code and Title are required'); return; }
    setIsLoading(true);
    try {
      const ddnCode = `DDN-${ddnForm.model_ident_code}-${ddnForm.sender || 'XX'}-${ddnForm.ddn_number}-${ddnForm.year_of_issue}`;
      await axios.post(`${API}/dml/${selectedDml.id}/entry`, {
        dmc: {
          model_ident_code: ddnForm.model_ident_code.toUpperCase(),
          system_diff_code: 'A',
          system_code: '00', sub_system_code: '0', sub_sub_system_code: '0',
          assy_code: '00', disassy_code: '00', disassy_code_variant: 'A',
          info_code: '00A', info_code_variant: 'A', item_location_code: 'D',
          learn_code: '', learn_event_code: '',
        },
        title: ddnForm.title, issue_number: '001',
        in_work: '00', security_classification: ddnForm.security_classification,
        schema: 'Data Dispatch Note',
        responsible_partner_company: '', originator: '',
        entry_type: 'ddn', ddn_code: ddnCode,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Data Dispatch Note added');
      refreshDml();
      setDdnForm(prev => ({ ...prev, title: '' }));
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to add DDN'); }
    finally { setIsLoading(false); }
  };

  const refreshDml = async () => {
    if (selectedDml) {
      const detail = await axios.get(`${API}/dml/${selectedDml.id}`, { headers: { Authorization: `Bearer ${token}` } });
      setSelectedDml(detail.data);
    }
  };

  // ── DDN persistence ──
  useEffect(() => {
    if (currentProjectId && token) {
      loadDdn();
    } else {
      setSavedDdn(null);
    }
  }, [currentProjectId, token]);

  const loadDdn = async () => {
    try {
      const resp = await axios.get(`${API}/ddn?project_id=${currentProjectId}`, { headers: { Authorization: `Bearer ${token}` } });
      if (resp.data.length > 0) {
        const ddn = resp.data[0];
        setSavedDdn(ddn);
        // Populate form from saved DDN
        setDdnForm({
          model_ident_code: ddn.model_ident_code || '',
          sender: ddn.sender_ident || '',
          sender_cage: '',
          ddn_number: ddn.seq_number || '0001',
          receiver: ddn.receiver_ident || '',
          receiver_cage: '',
          year_of_issue: ddn.year_of_data_issue || String(currentYear),
          title: ddn.remarks || '',
          security_classification: ddn.security_classification || '',
        });
        setDdnDispatchTo(ddn.dispatch_to || { enterprise_name: '', street: '', postal_zip_code: '', city: '', country: '' });
        setDdnDispatchFrom(ddn.dispatch_from || { enterprise_name: '', street: '', postal_zip_code: '', city: '', country: '' });
        setDdnDeliveryFiles(ddn.delivery_files || []);
      }
    } catch { /* no DDN yet */ }
  };

  const saveDdnInfo = async () => {
    if (!currentProjectId) { toast.error('Select a project first'); return; }
    if (!ddnForm.model_ident_code) { toast.error('Model Ident Code is required'); return; }
    setIsLoading(true);
    try {
      const payload = {
        project_id: currentProjectId,
        model_ident_code: ddnForm.model_ident_code,
        sender_ident: ddnForm.sender || '',
        receiver_ident: ddnForm.receiver || '',
        year_of_data_issue: ddnForm.year_of_issue,
        seq_number: ddnForm.ddn_number,
        security_classification: ddnForm.security_classification,
        dispatch_to: ddnDispatchTo,
        dispatch_from: ddnDispatchFrom,
        authorization: '',
        remarks: ddnForm.title,
        media_label: 'delivery',
        delivery_files: ddnDeliveryFiles,
      };
      if (savedDdn) {
        await axios.put(`${API}/ddn/${savedDdn.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('DDN information updated');
        setSavedDdn({ ...savedDdn, ...payload });
      } else {
        const resp = await axios.post(`${API}/ddn`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('DDN created');
        setSavedDdn({ id: resp.data.id, ...payload });
      }
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to save DDN'); }
    finally { setIsLoading(false); }
  };

  // Scan selected DM/PM XML files for ICN references and resolve to actual ICN files
  const collectReferencedIcns = async (selectedNames) => {
    const icnNames = new Set();
    for (const name of selectedNames) {
      const upper = name.toUpperCase();
      if (!upper.startsWith('DMC-') && !upper.startsWith('PMC-')) continue;
      const f = resolveDirFile(name);
      if (!f || !f.name.toLowerCase().endsWith('.xml')) continue;
      try {
        const file = await f.handle.getFile();
        const text = await file.text();
        const refs = text.match(/ICN-[A-Za-z0-9._-]+/g) || [];
        for (const r of refs) {
          const rUpper = r.toUpperCase().replace(/\.$/, '');
          const hit = dirIcns.find(ic => {
            const icUpper = ic.name.toUpperCase();
            return icUpper === rUpper || icUpper.startsWith(rUpper + '.') || rUpper.startsWith(icUpper.replace(/\.[^.]+$/, ''));
          });
          if (hit) icnNames.add(hit.name);
        }
      } catch { /* file unreadable, skip */ }
    }
    return [...icnNames];
  };

  const openDdnHistory = async () => {
    setShowDdnHistory(true);
    setDdnHistoryLoading(true);
    try {
      const resp = await axios.get(`${API}/ddn/history/${currentProjectId}`, { headers: { Authorization: `Bearer ${token}` } });
      setDdnHistory(resp.data || []);
    } catch {
      toast.error('Failed to load DDN history');
    } finally {
      setDdnHistoryLoading(false);
    }
  };

  const reopenDdnBuild = async (build) => {
    try {
      const resp = await axios.get(`${API}/ddn/history/build/${build.id}/xml`, { headers: { Authorization: `Bearer ${token}` } });
      setDdnExportContent(resp.data.xml);
      setDdnExportFilename(resp.data.filename);
      setDdnDeliveryFiles(resp.data.delivery_files || []);
      setShowDdnHistory(false);
      setShowDdnExport(true);
    } catch {
      toast.error('Failed to load DDN build');
    }
  };

  const ddnValidNames = () => new Set([
    ...ddnCandidateFiles.candidates.map(c => c.filename),
    ...ddnCandidateFiles.icnFiles,
  ]);

  const openDdnBuilder = async () => {
    // prune stale selections (files no longer in the DML/directory), then pre-check ICNs
    // referenced by the selected DMs so the user can see and untick them before building
    const valid = ddnValidNames();
    let selection = ddnDeliveryFiles.filter(n => valid.has(n));
    try {
      const refIcns = await collectReferencedIcns(selection);
      selection = [...new Set([...selection, ...refIcns])];
    } catch { /* directory unavailable */ }
    setDdnDeliveryFiles(selection);
    setShowDdnBuilder(true);
  };

  const buildDdnXml = async () => {
    if (!savedDdn) { toast.error('Save DDN information first'); return; }
    setDdnBuilding(true);
    try {
      // Honour exactly what is ticked in the dialog
      const valid = ddnValidNames();
      const selected = ddnDeliveryFiles.filter(n => valid.has(n));
      if (selected.length !== ddnDeliveryFiles.length) setDdnDeliveryFiles(selected);
      if (selected.length === 0) { toast.error('Select at least one file to include in the DDN'); setDdnBuilding(false); return; }
      // Update delivery files first
      await axios.put(`${API}/ddn/${savedDdn.id}`, { delivery_files: selected }, { headers: { Authorization: `Bearer ${token}` } });
      const resp = await axios.post(`${API}/ddn/${savedDdn.id}/build`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setDdnExportContent(resp.data.xml);
      setDdnExportFilename(resp.data.filename);
      setShowDdnBuilder(false);
      setShowDdnExport(true);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to build DDN'); }
    finally { setDdnBuilding(false); }
  };

  const downloadDdnXml = () => {
    const blob = new Blob([ddnExportContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = ddnExportFilename; a.click();
    URL.revokeObjectURL(url);
    toast.success('DDN XML downloaded');
  };

  // Resolve a declared delivery filename to an actual working-directory file (tolerant match)
  const resolveDirFile = (name) => {
    const all = [...dirFiles, ...dirIcns];
    const target = name.toUpperCase();
    let f = all.find(x => x.name.toUpperCase() === target);
    if (f) return f;
    const base = target.replace(/\.XML$/i, '');
    f = all.find(x => x.name.toUpperCase().replace(/\.XML$/i, '') === base);
    if (f) return f;
    // fall back to the DMC/PMC/ICN code portion (before the _issue-language suffix)
    const code = target.split('_')[0];
    if (code.length > 8) {
      f = all.find(x => x.name.toUpperCase().startsWith(code));
      if (f) return f;
    }
    return null;
  };

  const downloadDdnZip = async () => {
    setDdnZipBuilding(true);
    try {
      const JSZip = (await import('jszip')).default;
      const zip = new JSZip();
      zip.file(ddnExportFilename, ddnExportContent);
      const missing = [];
      for (const name of ddnDeliveryFiles) {
        const f = resolveDirFile(name);
        if (!f) { missing.push(name); continue; }
        try {
          const file = await f.handle.getFile();
          // store under the declared delivery name so the package matches the DDN XML
          zip.file(name, await file.arrayBuffer());
        } catch {
          missing.push(name);
        }
      }
      const blob = await zip.generateAsync({ type: 'blob' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = ddnExportFilename.replace(/\.xml$/i, '') + '.zip';
      a.click();
      URL.revokeObjectURL(url);
      if (missing.length > 0) {
        toast.warning(`ZIP created, but ${missing.length} file${missing.length !== 1 ? 's were' : ' was'} not found in the working directory`, {
          description: missing.slice(0, 5).join('\n') + (missing.length > 5 ? `\n…and ${missing.length - 5} more` : ''),
          duration: 8000,
        });
      } else {
        toast.success('DDN package (ZIP) downloaded');
      }
    } catch (error) {
      toast.error('Failed to build DDN ZIP');
    } finally {
      setDdnZipBuilding(false);
    }
  };

  const copyDdnToClipboard = () => {
    navigator.clipboard.writeText(ddnExportContent);
    toast.success('Copied to clipboard');
  };

  const saveDmlInfo = async () => {
    if (!selectedDml) return;
    setIsLoading(true);
    try {
      await axios.put(`${API}/dml/${selectedDml.id}`, {
        model_ident_code: dmlInfoForm.model_ident_code,
        sender: dmlInfoForm.sender,
        sender_cage: dmlInfoForm.sender_cage,
        dml_type: dmlInfoForm.dml_type,
        year_of_issue: dmlInfoForm.year_of_issue,
        sequence_number: dmlInfoForm.sequence_number,
        issue_number: dmlInfoForm.issue_number,
        in_work: dmlInfoForm.in_work,
        security_classification: dmlInfoForm.security_classification,
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Information saved');
      refreshDml();
    } catch (error) { toast.error('Failed to save'); }
    finally { setIsLoading(false); }
  };

  const exportDml = async () => {
    if (!selectedDml) return;
    try {
      const response = await axios.get(`${API}/dml/${selectedDml.id}/export`, { headers: { Authorization: `Bearer ${token}` } });
      setExportContent(response.data.content);
      setExportFilename(response.data.filename);
      setShowExportDialog(true);
    } catch (error) { toast.error('Failed to export DML'); }
  };

  const exportDmShells = async () => {
    if (!selectedDml) return;
    try {
      const response = await axios.get(`${API}/dml/${selectedDml.id}/export-shells`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob',
      });
      const blob = new Blob([response.data], { type: 'application/zip' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const disposition = response.headers['content-disposition'];
      const filename = disposition
        ? disposition.split('filename=')[1]?.replace(/"/g, '')
        : 'DM_Shells.zip';
      a.download = filename;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('DM Shells exported as ZIP');
    } catch (error) { toast.error('Failed to export DM shells'); }
  };

  const downloadXml = () => {
    const blob = new Blob([exportContent], { type: 'application/xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = exportFilename;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('XML downloaded');
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(exportContent);
    toast.success('Copied to clipboard');
  };

  const formatDmcPreview = () => {
    const parts = [
      `DMC-${dmcForm.model_ident_code || '???'}`,
      dmcForm.system_diff_code || '?',
      dmcForm.system_code || '00',
      `${dmcForm.sub_system_code || '0'}${dmcForm.sub_sub_system_code || '0'}`,
      dmcForm.assy_code || '00',
      `${dmcForm.disassy_code || '00'}${dmcForm.disassy_code_variant || 'A'}`,
      `${dmcForm.info_code || '000'}${dmcForm.info_code_variant || 'A'}`,
      dmcForm.item_location_code || 'A',
    ];
    return parts.join('-');
  };

  // Build sender options from project companies
  const senderOptions = useMemo(() => {
    const opts = [];
    (currentProject?.responsible_partner_companies || []).forEach(c => {
      opts.push({ value: c.company_name, label: `${c.company_name} (${c.cage_code})`, cage: c.cage_code });
    });
    (currentProject?.originators || []).forEach(c => {
      if (!opts.find(o => o.value === c.company_name)) {
        opts.push({ value: c.company_name, label: `${c.company_name} (${c.cage_code})`, cage: c.cage_code });
      }
    });
    return opts;
  }, [currentProject]);

  // ── Working Directory functions ──
  const scanDirectory = useCallback(async (handle) => {
    if (!handle) return;
    setScanningDir(true);
    try {
      const files = [];
      const icns = [];
      for await (const { entry, path } of walkDirectory(handle)) {
        if (entry.name.toLowerCase().endsWith('.xml')) {
          try {
            const file = await entry.getFile();
            files.push({
              name: entry.name,
              handle: entry,
              path,
              size: file.size,
              lastModified: file.lastModified,
            });
          } catch {
            files.push({ name: entry.name, handle: entry, path, size: 0, lastModified: 0 });
          }
        } else if (entry.name.toUpperCase().startsWith('ICN-')) {
          icns.push({ name: entry.name, handle: entry, path });
        }
      }
      files.sort((a, b) => a.name.localeCompare(b.name));
      icns.sort((a, b) => a.name.localeCompare(b.name));
      setDirFiles(files);
      setDirIcns(icns);
    } catch (err) {
      toast.error('Failed to read directory');
      setDirFiles([]);
      setDirIcns([]);
    } finally {
      setScanningDir(false);
    }
  }, []);

  const linkWorkingDirectory = async () => {
    if (!window.showDirectoryPicker) {
      toast.error('Your browser does not support the File System Access API. Please use Chrome or Edge.');
      return;
    }
    try {
      const handle = await window.showDirectoryPicker({ mode: 'read' });
      setDirHandle(handle);
      setDirName(handle.name);
      await scanDirectory(handle);
      // Persist per-project (shared store with Project Settings)
      if (currentProjectId) {
        try {
          await saveDirectoryHandle(currentProjectId, handle, handle.name);
          await axios.put(`${API}/projects/${currentProjectId}`, { working_directory: handle.name }, { headers: { Authorization: `Bearer ${token}` } });
        } catch { /* non-critical */ }
      }
      toast.success(`Linked to "${handle.name}"`);
    } catch (err) {
      if (err.name !== 'AbortError') {
        toast.error('Failed to access directory');
      }
    }
  };

  const refreshDirectory = async () => {
    if (dirHandle) {
      try {
        let perm = await dirHandle.queryPermission({ mode: 'read' });
        if (perm !== 'granted') {
          perm = await dirHandle.requestPermission({ mode: 'read' });
          if (perm !== 'granted') { toast.error('Permission denied'); return; }
        }
        await scanDirectory(dirHandle);
        toast.success('Directory refreshed');
      } catch {
        toast.error('Directory access lost. Please re-link.');
        setDirHandle(null);
        setDirFiles([]);
        setDirIcns([]);
        setDirName('');
      }
    }
  };

  const unlinkDirectory = async () => {
    setDirHandle(null);
    setDirFiles([]);
    setDirIcns([]);
    setDirName('');
    if (currentProjectId) {
      try {
        await removeDirectoryHandle(currentProjectId);
        await axios.put(`${API}/projects/${currentProjectId}`, { working_directory: '' }, { headers: { Authorization: `Bearer ${token}` } });
      } catch { /* non-critical */ }
    }
    toast.info('Working directory unlinked');
  };

  // Load the working directory linked to the SELECTED PROJECT (from Project Settings store)
  useEffect(() => {
    (async () => {
      setDirHandle(null);
      setDirFiles([]);
      setDirIcns([]);
      setDirName('');
      if (!currentProjectId) return;
      try {
        const record = await getDirectoryHandle(currentProjectId);
        const handle = record?.handle;
        if (handle) {
          setDirHandle(handle);
          setDirName(record.displayName || handle.name);
          const perm = await handle.queryPermission({ mode: 'read' });
          if (perm === 'granted') {
            scanDirectory(handle);
          }
        }
      } catch { /* no saved handle for this project */ }
    })();
  }, [currentProjectId, scanDirectory]);

  // Match DML entries against working directory files
  const viewListData = useMemo(() => {
    const entries = selectedDml?.entries || [];
    const fileNames = new Set(dirFiles.map(f => f.name.toUpperCase()));
    const matchedFiles = new Set();

    const entriesWithStatus = entries.map((entry, index) => {
      const entryType = entry.entry_type || 'dm';
      let codeBase = '';
      let displayCode = '';

      if (entryType === 'pm' && entry.pm_code) {
        codeBase = entry.pm_code.toUpperCase();
        displayCode = entry.pm_code;
      } else if (entryType === 'ddn' && entry.ddn_code) {
        codeBase = entry.ddn_code.toUpperCase();
        displayCode = entry.ddn_code;
      } else {
        // Build expected filename pattern from DMC
        const dmc = entry.dmc || {};
        const mic = dmc.model_ident_code || '';
        const sdc = dmc.system_diff_code || '';
        const sc = dmc.system_code || '';
        const ssc = dmc.sub_system_code || '0';
        const sssc = dmc.sub_sub_system_code || '0';
        const ac = dmc.assy_code || '';
        const dc = dmc.disassy_code || '';
        const dcv = dmc.disassy_code_variant || '';
        const ic = dmc.info_code || '';
        const icv = dmc.info_code_variant || '';
        const ilc = dmc.item_location_code || '';
        codeBase = `DMC-${mic}-${sdc}-${sc}-${ssc}${sssc}-${ac}-${dc}${dcv}-${ic}${icv}-${ilc}`.toUpperCase();
        displayCode = entry.dmc_formatted || codeBase;
      }

      // Check if any file in the directory starts with this code pattern
      let fileMatch = null;
      for (const f of dirFiles) {
        const fUpper = f.name.toUpperCase();
        if (fUpper.startsWith(codeBase)) {
          fileMatch = f;
          matchedFiles.add(fUpper);
          break;
        }
      }

      return { ...entry, index, fileMatch, dmcBase: codeBase, display_code: displayCode };
    });

    // Files in directory that don't match any DML entry
    const unmatchedFiles = dirFiles.filter(f => !matchedFiles.has(f.name.toUpperCase()));

    return { entriesWithStatus, unmatchedFiles };
  }, [selectedDml, dirFiles]);

  // DDN builder: available files from DML entries + ICNs from working dir
  const ddnCandidateFiles = useMemo(() => {
    const candidates = [];
    const entries = selectedDml?.entries || [];

    // DMs and PMs from DML entries (build filenames from DMC/PMC codes)
    entries.forEach((entry) => {
      const entryType = entry.entry_type || 'dm';
      let filename = '';
      if (entryType === 'pm' && entry.pm_code) {
        const issNum = entry.issue_number || '001';
        const inWork = entry.in_work || '00';
        filename = `${entry.pm_code}_${issNum}-${inWork}_SX-US.XML`;
      } else if (entryType === 'ddn' && entry.ddn_code) {
        filename = `${entry.ddn_code}.XML`;
      } else {
        filename = entry.dmc_formatted || '';
        if (filename) {
          const issNum = entry.issue_number || '001';
          const inWork = entry.in_work || '00';
          filename = `${filename}_${issNum}-${inWork}_EN-US.XML`;
        }
      }
      if (filename) {
        candidates.push({ filename, title: entry.title || '', type: entryType === 'pm' ? 'PM' : entryType === 'ddn' ? 'DDN' : 'DM', fromDml: true });
      }
    });

    // Also include matched files from working directory (actual filenames)
    if (dirFiles.length > 0) {
      dirFiles.forEach((f) => {
        const upper = f.name.toUpperCase();
        // Only add if not already covered by DML entries
        const alreadyCovered = candidates.some(c => upper.startsWith(c.filename.split('_')[0].toUpperCase()));
        if (!alreadyCovered) {
          let type = 'File';
          if (upper.startsWith('DMC-')) type = 'DM';
          else if (upper.startsWith('PMC-')) type = 'PM';
          else if (upper.startsWith('DML-')) type = 'DML';
          candidates.push({ filename: f.name, title: '', type, fromDml: false });
        }
      });
    }

    // ICNs from working directory (graphic files starting with ICN-)
    const icnFiles = dirIcns.map(f => f.name);

    return { candidates, icnFiles };
  }, [selectedDml, dirFiles, dirIcns]);

  // ========================= RENDER =========================

  // Header JSX rendered inline
  const headerJsx = (
    <div className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between -mx-6 -mt-6 mb-6 rounded-t-lg" data-testid="dml-header">
      <Button variant="ghost" onClick={() => navigate(currentProjectId ? `/dashboard/projects/${currentProjectId}` : '/dashboard/projects')}
        className="text-white hover:bg-slate-800 hover:text-white" data-testid="back-to-tools-btn">
        <ArrowLeft className="w-4 h-4 mr-2" /> {currentProjectId ? 'Back to Project' : 'Back to Projects'}
      </Button>
      {currentProjectName && (
        <span className="text-sm text-slate-300">Project: <span className="font-semibold text-white">{currentProjectName}</span></span>
      )}
    </div>
  );

  // If no project selected yet (customers must select, admins can skip)
  if (!currentProjectId && !isAdmin) {
    return (
      <div className="space-y-6" data-testid="dml-builder-page">
        {headerJsx}
        <Card className="border-2 border-violet-300 bg-violet-50/30">
          <CardContent className="p-6">
            <div className="flex items-center gap-4 flex-wrap">
              <label className="text-sm font-medium text-slate-700">Select Project <span className="text-red-500">*</span></label>
              <Select value={selectedProject} onValueChange={(value) => {
                setSelectedProject(value);
                const project = projects.find(p => p.id === value);
                setCurrentProjectName(project?.title || null);
                setCurrentProject(project || null);
                setCurrentProjectId(value === 'none' ? null : value);
                setSelectedDml(null);
              }}>
                <SelectTrigger className="w-[300px]" data-testid="project-select">
                  <SelectValue placeholder="Select a project..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none" className="text-slate-400">-- Select a project --</SelectItem>
                  {projects.map(p => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <p className="text-sm text-violet-700 mt-3">A project must be selected to build and manage DMLs</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-0" data-testid="dml-builder-page">
      {headerJsx}

      {/* Project selector (compact) */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <Select value={selectedProject} onValueChange={(value) => {
          setSelectedProject(value);
          const project = projects.find(p => p.id === value);
          setCurrentProjectName(project?.title || null);
          setCurrentProject(project || null);
          setCurrentProjectId(value === 'none' ? null : value);
          setSelectedDml(null);
        }}>
          <SelectTrigger className="w-[280px]" data-testid="project-select">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="none" className="text-slate-400">{isAdmin ? '-- No Project (Testing) --' : '-- Select a project --'}</SelectItem>
            {projects.map(p => (<SelectItem key={p.id} value={p.id}>{p.title}</SelectItem>))}
          </SelectContent>
        </Select>
        {currentProjectId && (dirName ? (
          <Badge variant="outline" className="bg-violet-50 text-violet-700 border-violet-200 gap-1.5 max-w-[240px]" data-testid="wd-chip">
            <FolderOpen className="w-3 h-3 shrink-0" /><span className="truncate font-mono text-xs">{dirName}</span>
          </Badge>
        ) : (
          <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300 gap-1.5" data-testid="wd-chip">
            <FolderOpen className="w-3 h-3" /><span className="text-xs">No directory linked</span>
          </Badge>
        ))}
        {isAdmin && selectedProject === 'none' && <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-300">Testing Mode</Badge>}
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-transparent gap-2 p-0 h-auto mb-2 flex-wrap">
          <TabsTrigger value="add" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white bg-violet-500 text-white rounded-full px-5 py-2 text-sm font-medium" data-testid="tab-add-module">
            Add Data Module
          </TabsTrigger>
          <TabsTrigger value="add-pub" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white bg-violet-500 text-white rounded-full px-5 py-2 text-sm font-medium" data-testid="tab-add-pub-module">
            Add Publication Module
          </TabsTrigger>
          <TabsTrigger value="add-ddn" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white bg-violet-500 text-white rounded-full px-5 py-2 text-sm font-medium" data-testid="tab-add-ddn">
            Add Data Dispatch Note
          </TabsTrigger>
          <TabsTrigger value="info" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white bg-violet-500 text-white rounded-full px-5 py-2 text-sm font-medium" data-testid="tab-dml-info">
            DML Information
          </TabsTrigger>
          <TabsTrigger value="list" className="data-[state=active]:bg-violet-600 data-[state=active]:text-white bg-violet-500 text-white rounded-full px-5 py-2 text-sm font-medium" data-testid="tab-view-list">
            View List
          </TabsTrigger>
        </TabsList>

        {/* ============ TAB: DML Information ============ */}
        <TabsContent value="info" className="mt-4">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>DML Information</h2>

          <Card className="border-slate-200 bg-slate-50/50">
            <CardContent className="p-6 space-y-6">
              {/* Model Identification Code - full width */}
              <div>
                <FieldLabel label="Model Identification Code" tooltip="The alphanumeric code that identifies the product or system model. Sourced from project configuration." testId="mic" />
                {(currentProject?.model_ident_codes || []).length > 0 ? (
                  <Select value={dmlInfoForm.model_ident_code}
                    onValueChange={(v) => setDmlInfoForm(prev => ({ ...prev, model_ident_code: v }))}>
                    <SelectTrigger data-testid="dml-info-mic"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      {currentProject.model_ident_codes.map(code => (
                        <SelectItem key={code} value={code}>{code}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={dmlInfoForm.model_ident_code}
                    onChange={(e) => setDmlInfoForm(prev => ({ ...prev, model_ident_code: e.target.value.toUpperCase() }))}
                    placeholder="Select..." maxLength={14} className="uppercase font-mono" data-testid="dml-info-mic" />
                )}
              </div>

              {/* Sender Identification - full width */}
              <div>
                <FieldLabel label="Sender Identification" tooltip="The company or organisation sending this DML. Select from project companies or type manually." testId="sender" />
                {senderOptions.length > 0 ? (
                  <Select value={dmlInfoForm.sender || 'placeholder_none'}
                    onValueChange={(v) => {
                      if (v === 'placeholder_none') { setDmlInfoForm(prev => ({ ...prev, sender: '', sender_cage: '' })); return; }
                      const opt = senderOptions.find(o => o.value === v);
                      setDmlInfoForm(prev => ({ ...prev, sender: v, sender_cage: opt?.cage || '' }));
                    }}>
                    <SelectTrigger data-testid="dml-info-sender"><SelectValue placeholder="Select company..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder_none" className="text-slate-400">Select company...</SelectItem>
                      {senderOptions.map((o, i) => (
                        <SelectItem key={i} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : (
                  <Input value={dmlInfoForm.sender}
                    onChange={(e) => setDmlInfoForm(prev => ({ ...prev, sender: e.target.value }))}
                    placeholder="Select company..." data-testid="dml-info-sender" />
                )}
              </div>

              {/* DML Type + Year of Issue - 2 columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <FieldLabel label="DML Type" tooltip="The type of Data Module List: S (Status), P (Preliminary), or C (Changed)." testId="dml-type" />
                  <Select value={dmlInfoForm.dml_type || 'placeholder_none'}
                    onValueChange={(v) => setDmlInfoForm(prev => ({ ...prev, dml_type: v === 'placeholder_none' ? '' : v }))}>
                    <SelectTrigger data-testid="dml-info-type"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder_none" className="text-slate-400">Select...</SelectItem>
                      {DML_TYPES.map(t => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel label="Year of Issue" tooltip="The year in which this DML is issued." testId="year" />
                  <Select value={dmlInfoForm.year_of_issue}
                    onValueChange={(v) => setDmlInfoForm(prev => ({ ...prev, year_of_issue: v }))}>
                    <SelectTrigger data-testid="dml-info-year"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {YEAR_OPTIONS.map(y => (
                        <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Sequence Number + Issue Number - 2 columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <FieldLabel label="Sequence Number" tooltip="A sequential number identifying this DML within the series. Typically a 5-digit number." testId="seq" />
                  <Input value={dmlInfoForm.sequence_number}
                    onChange={(e) => setDmlInfoForm(prev => ({ ...prev, sequence_number: e.target.value }))}
                    placeholder="00001" maxLength={5} className="font-mono" data-testid="dml-info-seq" />
                </div>
                <div>
                  <FieldLabel label="Issue Number" tooltip="The issue number of this DML (e.g., 001)." testId="issue" />
                  <Select value={dmlInfoForm.issue_number || 'placeholder_none'}
                    onValueChange={(v) => setDmlInfoForm(prev => ({ ...prev, issue_number: v === 'placeholder_none' ? '' : v }))}>
                    <SelectTrigger data-testid="dml-info-issue"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder_none" className="text-slate-400">Select...</SelectItem>
                      {ISSUE_NUMBERS.slice(0, 20).map(n => (
                        <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* In Work + Security Classification - 2 columns */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <FieldLabel label="In Work" tooltip="The in-work status code. '00' typically indicates the final version." testId="inwork" />
                  <Select value={dmlInfoForm.in_work || 'placeholder_none'}
                    onValueChange={(v) => setDmlInfoForm(prev => ({ ...prev, in_work: v === 'placeholder_none' ? '' : v }))}>
                    <SelectTrigger data-testid="dml-info-inwork"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder_none" className="text-slate-400">Select...</SelectItem>
                      {IN_WORK_OPTIONS.slice(0, 20).map(n => (
                        <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel label="Security Classification" tooltip="The security classification level for this DML." testId="security" />
                  <div className={`rounded-md ${dmlInfoForm.security_classification ? 'bg-blue-50' : ''}`}>
                    <Select value={dmlInfoForm.security_classification}
                      onValueChange={(v) => setDmlInfoForm(prev => ({ ...prev, security_classification: v }))}>
                      <SelectTrigger data-testid="dml-info-security" className={dmlInfoForm.security_classification ? 'bg-blue-50 border-blue-200' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {SECURITY_CLASSIFICATIONS.map(s => (
                          <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Save & Export buttons */}
          <div className="mt-4 space-y-3">
            <Button onClick={saveDmlInfo} disabled={isLoading}
              className="w-full bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium py-6" variant="secondary"
              data-testid="save-dml-info-btn">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Information
            </Button>
            <Button onClick={exportDml} className="w-full bg-violet-600 hover:bg-violet-700 text-white font-medium py-6"
              data-testid="export-dml-btn">
              <Download className="w-4 h-4 mr-2" /> DML Export
            </Button>
          </div>
        </TabsContent>

        {/* ============ TAB: Add Data Module ============ */}
        <TabsContent value="add" className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              {editingIndex !== null ? 'Edit Data Module' : 'Add Data Module'}
            </h2>
            {editingIndex !== null && (
              <Button variant="outline" size="sm" onClick={cancelEdit} data-testid="cancel-edit-btn">
                Cancel Edit
              </Button>
            )}
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-6">
              {/* DMC Preview */}
              <div className="p-4 bg-slate-900 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">Generated DMC Preview</p>
                <code className="text-violet-400 font-mono text-base sm:text-lg break-all" data-testid="dmc-preview">
                  {formatDmcPreview()}
                </code>
              </div>

              {/* Schema */}
              <div>
                <FieldLabel label="Schema" tooltip="The S1000D data module schema type for this entry" testId="schema" />
                <Select value={dmcForm.schema || 'placeholder_none'}
                  onValueChange={(v) => setDmcForm(prev => ({ ...prev, schema: v === 'placeholder_none' ? '' : v }))}>
                  <SelectTrigger data-testid="schema-select"><SelectValue placeholder="Select schema..." /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="placeholder_none" className="text-slate-400">Select schema...</SelectItem>
                    {DM_SCHEMA_TYPES.map(v => (<SelectItem key={v} value={v}>{v}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>

              {/* Model Ident Code + System Diff Code */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel label="Model Identification Code" tooltip="From project configuration" />
                  {(currentProject?.model_ident_codes || []).length > 0 ? (
                    <Select value={dmcForm.model_ident_code}
                      onValueChange={(v) => setDmcForm(prev => ({ ...prev, model_ident_code: v }))}>
                      <SelectTrigger data-testid="mic-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        {currentProject.model_ident_codes.map(code => (
                          <SelectItem key={code} value={code}>{code}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={dmcForm.model_ident_code}
                      onChange={(e) => setDmcForm(prev => ({ ...prev, model_ident_code: e.target.value.toUpperCase() }))}
                      placeholder="e.g., DEMO" maxLength={14} className="uppercase font-mono" data-testid="mic-input" />
                  )}
                </div>
                <div>
                  <FieldLabel label="System Difference Code" tooltip="Identifies system variations" />
                  <Input value={dmcForm.system_diff_code}
                    onChange={(e) => setDmcForm(prev => ({ ...prev, system_diff_code: e.target.value.toUpperCase() }))}
                    placeholder="A" maxLength={4} className="uppercase font-mono" data-testid="sdc-input" />
                </div>
              </div>

              {/* System Code (SNS) */}
              <div>
                <FieldLabel label="System Code (SNS)" tooltip="Standard Numbering System code identifying the system" />
                <SearchableSelect options={ALL_SNS_CODES} value={dmcForm.system_code}
                  onChange={(v) => {
                    const newSubSystems = getSubSystemsForSystem(v);
                    const defaultSubSys = newSubSystems[0]?.code || '0';
                    setDmcForm(prev => ({ ...prev, system_code: v, sub_system_code: defaultSubSys, sub_sub_system_code: '0' }));
                  }}
                  placeholder="Search SNS codes..." label="SNS codes" testId="sns-select" />
              </div>

              {/* Sub-system, Sub-sub-system, Assembly */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <FieldLabel label="Sub-System Code" tooltip="Identifies the sub-system within the selected system code" />
                  <Select value={dmcForm.sub_system_code}
                    onValueChange={(v) => setDmcForm(prev => ({ ...prev, sub_system_code: v, sub_sub_system_code: '0' }))}>
                    <SelectTrigger data-testid="subsys-input"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {subSystemOptions.map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.code} - {c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel label="Sub-Sub-System Code" tooltip="Further breakdown within the selected sub-system" />
                  <Select value={dmcForm.sub_sub_system_code}
                    onValueChange={(v) => setDmcForm(prev => ({ ...prev, sub_sub_system_code: v }))}>
                    <SelectTrigger data-testid="subsubsys-input"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {subSubSystemOptions.map(c => (
                        <SelectItem key={c.code} value={c.code}>{c.code}{c.name && c.name !== c.code ? ` - ${c.name}` : ''}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel label="Assembly Code" />
                  <Input value={dmcForm.assy_code} onChange={(e) => setDmcForm(prev => ({ ...prev, assy_code: e.target.value }))}
                    placeholder="00" maxLength={4} className="font-mono" data-testid="assy-input" />
                </div>
              </div>

              {/* Disassembly Code + Variant */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel label="Disassembly Code" />
                  <Input value={dmcForm.disassy_code} onChange={(e) => setDmcForm(prev => ({ ...prev, disassy_code: e.target.value }))}
                    placeholder="00" maxLength={2} className="font-mono" data-testid="disassy-input" />
                </div>
                <div>
                  <FieldLabel label="Disassembly Code Variant" tooltip="Single letter A-Z identifying the variant" />
                  <Select value={dmcForm.disassy_code_variant}
                    onValueChange={(v) => setDmcForm(prev => ({ ...prev, disassy_code_variant: v }))}>
                    <SelectTrigger data-testid="disassy-var-input"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VARIANT_CODES.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Info Code + Variant */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel label="Information Code" tooltip="S1000D information code for the data module content type" />
                  <SearchableSelect options={INFO_CODES} value={dmcForm.info_code}
                    onChange={(v) => setDmcForm(prev => ({ ...prev, info_code: v }))}
                    placeholder="Search info codes..." label="info codes" testId="info-code-select" />
                </div>
                <div>
                  <FieldLabel label="Information Code Variant" tooltip="Single letter A-Z identifying the variant" />
                  <Select value={dmcForm.info_code_variant}
                    onValueChange={(v) => setDmcForm(prev => ({ ...prev, info_code_variant: v }))}>
                    <SelectTrigger data-testid="info-var-input"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {VARIANT_CODES.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Item Location + Learn Code */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <FieldLabel label="Item Location Code" />
                  <Select value={dmcForm.item_location_code}
                    onValueChange={(v) => setDmcForm(prev => ({ ...prev, item_location_code: v }))}>
                    <SelectTrigger data-testid="ilc-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {ITEM_LOCATION_CODES.map(c => (<SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel label="Learn Code (Optional)" />
                  <Input value={dmcForm.learn_code}
                    onChange={(e) => setDmcForm(prev => ({ ...prev, learn_code: e.target.value.toUpperCase() }))}
                    placeholder="" maxLength={3} className="uppercase font-mono" data-testid="learn-code-input" />
                </div>
                <div>
                  <FieldLabel label="Learn Event Code (Optional)" />
                  <Input value={dmcForm.learn_event_code}
                    onChange={(e) => setDmcForm(prev => ({ ...prev, learn_event_code: e.target.value.toUpperCase() }))}
                    placeholder="" maxLength={1} className="uppercase font-mono" data-testid="learn-event-input" />
                </div>
              </div>

              <div className="border-t pt-4" />

              {/* Module Metadata */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel label="Data Module Title *" />
                  <Input value={dmcForm.title}
                    onChange={(e) => setDmcForm(prev => ({ ...prev, title: e.target.value }))}
                    placeholder="e.g., General description" data-testid="module-title-input" />
                </div>
                <div>
                  <FieldLabel label="Security Classification" tooltip="From project settings" />
                  <Select value={dmcForm.security_classification}
                    onValueChange={(v) => setDmcForm(prev => ({ ...prev, security_classification: v }))}>
                    <SelectTrigger data-testid="security-select"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {SECURITY_CLASSIFICATIONS.map(s => (<SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div>
                  <FieldLabel label="Issue Number" />
                  <Input value={dmcForm.issue_number}
                    onChange={(e) => setDmcForm(prev => ({ ...prev, issue_number: e.target.value }))}
                    placeholder="001" maxLength={3} className="font-mono" data-testid="issue-num-input" />
                </div>
                <div>
                  <FieldLabel label="In Work" />
                  <Input value={dmcForm.in_work}
                    onChange={(e) => setDmcForm(prev => ({ ...prev, in_work: e.target.value }))}
                    placeholder="00" maxLength={2} className="font-mono" data-testid="in-work-input" />
                </div>
                <div>
                  <FieldLabel label="Responsible Partner Company" />
                  {(currentProject?.responsible_partner_companies || []).length > 0 ? (
                    <Select value={dmcForm.responsible_partner_company || '__none__'}
                      onValueChange={(v) => setDmcForm(prev => ({ ...prev, responsible_partner_company: v }))}>
                      <SelectTrigger data-testid="rpc-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        {currentProject.responsible_partner_companies.map((c, i) => (
                          <SelectItem key={i} value={c.company_name}>{c.company_name} ({c.cage_code})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={dmcForm.responsible_partner_company}
                      onChange={(e) => setDmcForm(prev => ({ ...prev, responsible_partner_company: e.target.value }))}
                      placeholder="Company name" data-testid="rpc-input" />
                  )}
                </div>
                <div>
                  <FieldLabel label="Originator" />
                  {(currentProject?.originators || []).length > 0 ? (
                    <Select value={dmcForm.originator || '__none__'}
                      onValueChange={(v) => setDmcForm(prev => ({ ...prev, originator: v }))}>
                      <SelectTrigger data-testid="originator-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">-- None --</SelectItem>
                        {currentProject.originators.map((c, i) => (
                          <SelectItem key={i} value={c.company_name}>{c.company_name} ({c.cage_code})</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={dmcForm.originator}
                      onChange={(e) => setDmcForm(prev => ({ ...prev, originator: e.target.value }))}
                      placeholder="Originator name" data-testid="originator-input" />
                  )}
                </div>
              </div>

              <Button className="w-full bg-violet-600 hover:bg-violet-700 py-6" onClick={addEntry}
                disabled={isLoading || !selectedDml} data-testid="add-module-btn">
                {isLoading ? (<><Loader2 className="mr-2 h-4 w-4 animate-spin" /> {editingIndex !== null ? 'Updating...' : 'Adding...'}</>)
                  : editingIndex !== null
                    ? (<><Pencil className="w-4 h-4 mr-2" /> Update Data Module</>)
                    : (<><Plus className="w-4 h-4 mr-2" /> Add Data Module</>)}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ TAB: Add Publication Module ============ */}
        <TabsContent value="add-pub" className="mt-4">
          <h2 className="text-2xl font-bold text-slate-900 mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>Add Publication Module</h2>
          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-6">
              <div className="p-4 bg-slate-900 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">Generated PMC Preview</p>
                <code className="text-violet-400 font-mono text-base" data-testid="pmc-preview">
                  PMC-{pubForm.model_ident_code || '???'}-{pubForm.pm_issuer || 'XX'}-{pubForm.pm_number}-{pubForm.pm_volume}
                </code>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel label="Model Identification Code" />
                  {(currentProject?.model_ident_codes || []).length > 0 ? (
                    <Select value={pubForm.model_ident_code}
                      onValueChange={(v) => setPubForm(prev => ({ ...prev, model_ident_code: v }))}>
                      <SelectTrigger data-testid="pub-mic-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>{currentProject.model_ident_codes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input value={pubForm.model_ident_code} onChange={e => setPubForm(prev => ({ ...prev, model_ident_code: e.target.value.toUpperCase() }))}
                      placeholder="e.g., DEMO" maxLength={14} className="uppercase font-mono" data-testid="pub-mic-input" />
                  )}
                </div>
                <div>
                  <FieldLabel label="PM Issuer" tooltip="Company or organisation code issuing this publication module" />
                  <Input value={pubForm.pm_issuer} onChange={e => setPubForm(prev => ({ ...prev, pm_issuer: e.target.value.toUpperCase() }))}
                    placeholder="e.g., TIMCO" className="uppercase font-mono" data-testid="pub-issuer-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <FieldLabel label="PM Number" tooltip="Sequential number for this publication module" />
                  <Input value={pubForm.pm_number} onChange={e => setPubForm(prev => ({ ...prev, pm_number: e.target.value }))}
                    placeholder="00001" maxLength={5} className="font-mono" data-testid="pub-number-input" />
                </div>
                <div>
                  <FieldLabel label="Volume" />
                  <Input value={pubForm.pm_volume} onChange={e => setPubForm(prev => ({ ...prev, pm_volume: e.target.value }))}
                    placeholder="00" maxLength={2} className="font-mono" data-testid="pub-volume-input" />
                </div>
                <div>
                  <FieldLabel label="Security Classification" />
                  <Select value={pubForm.security_classification || 'placeholder_none'}
                    onValueChange={v => setPubForm(prev => ({ ...prev, security_classification: v === 'placeholder_none' ? '' : v }))}>
                    <SelectTrigger data-testid="pub-security"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder_none" className="text-slate-400">Select...</SelectItem>
                      {SECURITY_CLASSIFICATIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <FieldLabel label="Publication Module Title *" />
                <Input value={pubForm.title} onChange={e => setPubForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Technical Manual" data-testid="pub-title-input" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <FieldLabel label="Issue Number" />
                  <Input value={pubForm.issue_number} onChange={e => setPubForm(prev => ({ ...prev, issue_number: e.target.value }))}
                    placeholder="001" maxLength={3} className="font-mono" data-testid="pub-issue-input" />
                </div>
                <div>
                  <FieldLabel label="In Work" />
                  <Input value={pubForm.in_work} onChange={e => setPubForm(prev => ({ ...prev, in_work: e.target.value }))}
                    placeholder="00" maxLength={2} className="font-mono" data-testid="pub-inwork-input" />
                </div>
              </div>
              <Button className="w-full bg-violet-600 hover:bg-violet-700 py-6" onClick={addPubModule}
                disabled={isLoading || !selectedDml} data-testid="add-pub-btn">
                {isLoading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Publication Module
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* ============ TAB: Add Data Dispatch Note ============ */}
        <TabsContent value="add-ddn" className="mt-4">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>Data Dispatch Note</h2>
            {savedDdn && (
              <Badge className="bg-emerald-100 text-emerald-700 border-emerald-300">
                <Check className="w-3 h-3 mr-1" /> Saved
              </Badge>
            )}
          </div>

          <Card className="border-slate-200">
            <CardContent className="p-6 space-y-6">
              {/* DDN Code Preview */}
              <div className="p-4 bg-slate-900 rounded-lg">
                <p className="text-xs text-slate-400 mb-1">Generated DDN Code Preview</p>
                <code className="text-violet-400 font-mono text-base" data-testid="ddn-preview">
                  DDN-{ddnForm.model_ident_code || '???'}-{ddnForm.sender || 'XX'}-{ddnForm.receiver || 'XX'}-{ddnForm.year_of_issue}-{ddnForm.ddn_number}
                </code>
              </div>

              {/* Row 1: Model Ident + DDN Number */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel label="Model Identification Code" />
                  {(currentProject?.model_ident_codes || []).length > 0 ? (
                    <Select value={ddnForm.model_ident_code}
                      onValueChange={v => setDdnForm(prev => ({ ...prev, model_ident_code: v }))}>
                      <SelectTrigger data-testid="ddn-mic-select"><SelectValue placeholder="Select..." /></SelectTrigger>
                      <SelectContent>{currentProject.model_ident_codes.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                    </Select>
                  ) : (
                    <Input value={ddnForm.model_ident_code} onChange={e => setDdnForm(prev => ({ ...prev, model_ident_code: e.target.value.toUpperCase() }))}
                      placeholder="e.g., DEMO" maxLength={14} className="uppercase font-mono" data-testid="ddn-mic-input" />
                  )}
                </div>
                <div>
                  <FieldLabel label="Sequence Number" tooltip="Sequential dispatch note number (5 digits)" />
                  <Input value={ddnForm.ddn_number} onChange={e => setDdnForm(prev => ({ ...prev, ddn_number: e.target.value }))}
                    placeholder="00001" maxLength={5} className="font-mono" data-testid="ddn-number-input" />
                </div>
              </div>

              {/* Row 2: Sender + Receiver */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel label="Sender Ident" tooltip="CAGE code or identifier of the dispatching organisation" />
                  {senderOptions.length > 0 ? (
                    <Select value={senderOptions.find(o => o.cage === ddnForm.sender || o.value === ddnForm.sender)?.value || 'placeholder_none'}
                      onValueChange={v => { if (v === 'placeholder_none') { setDdnForm(prev => ({ ...prev, sender: '', sender_cage: '' })); return; }
                        const opt = senderOptions.find(o => o.value === v);
                        setDdnForm(prev => ({ ...prev, sender: opt?.cage || v, sender_cage: opt?.cage || '' }));
                        setDdnDispatchFrom(prev => ({ ...prev, enterprise_name: v }));
                      }}>
                      <SelectTrigger data-testid="ddn-sender"><SelectValue placeholder="Select company..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="placeholder_none" className="text-slate-400">Select company...</SelectItem>
                        {senderOptions.map((o, i) => <SelectItem key={i} value={o.value}>{o.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : (
                    <Input value={ddnForm.sender} onChange={e => setDdnForm(prev => ({ ...prev, sender: e.target.value }))}
                      placeholder="Sender CAGE code" data-testid="ddn-sender-input" />
                  )}
                </div>
                <div>
                  <FieldLabel label="Receiver Ident" tooltip="CAGE code or identifier of the receiving organisation" />
                  <Input value={ddnForm.receiver} onChange={e => setDdnForm(prev => ({ ...prev, receiver: e.target.value }))}
                    placeholder="Receiver CAGE code" data-testid="ddn-receiver-input" />
                </div>
              </div>

              {/* Row 3: Year + Security */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <FieldLabel label="Year of Data Issue" />
                  <Select value={ddnForm.year_of_issue} onValueChange={v => setDdnForm(prev => ({ ...prev, year_of_issue: v }))}>
                    <SelectTrigger data-testid="ddn-year"><SelectValue /></SelectTrigger>
                    <SelectContent>{YEAR_OPTIONS.map(y => <SelectItem key={y.value} value={y.value}>{y.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <FieldLabel label="Security Classification" />
                  <Select value={ddnForm.security_classification || 'placeholder_none'}
                    onValueChange={v => setDdnForm(prev => ({ ...prev, security_classification: v === 'placeholder_none' ? '' : v }))}>
                    <SelectTrigger data-testid="ddn-security"><SelectValue placeholder="Select..." /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="placeholder_none" className="text-slate-400">Select...</SelectItem>
                      {SECURITY_CLASSIFICATIONS.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Remarks / Title */}
              <div>
                <FieldLabel label="DDN Remarks / Title" />
                <Input value={ddnForm.title} onChange={e => setDdnForm(prev => ({ ...prev, title: e.target.value }))}
                  placeholder="e.g., Initial Data Delivery" data-testid="ddn-title-input" />
              </div>

              {/* Dispatch To Address */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <Send className="w-4 h-4 text-violet-600" /> Dispatch To (Receiver Address)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel label="Enterprise Name" />
                    <Input value={ddnDispatchTo.enterprise_name} onChange={e => setDdnDispatchTo(prev => ({ ...prev, enterprise_name: e.target.value }))}
                      placeholder="Organisation name" data-testid="ddn-to-enterprise" />
                  </div>
                  <div>
                    <FieldLabel label="Street" />
                    <Input value={ddnDispatchTo.street} onChange={e => setDdnDispatchTo(prev => ({ ...prev, street: e.target.value }))}
                      placeholder="Street address" data-testid="ddn-to-street" />
                  </div>
                  <div>
                    <FieldLabel label="City" />
                    <Input value={ddnDispatchTo.city} onChange={e => setDdnDispatchTo(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="City" data-testid="ddn-to-city" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <FieldLabel label="Postal Code" />
                      <Input value={ddnDispatchTo.postal_zip_code} onChange={e => setDdnDispatchTo(prev => ({ ...prev, postal_zip_code: e.target.value }))}
                        placeholder="Postal/Zip" data-testid="ddn-to-postal" />
                    </div>
                    <div>
                      <FieldLabel label="Country" />
                      <Input value={ddnDispatchTo.country} onChange={e => setDdnDispatchTo(prev => ({ ...prev, country: e.target.value }))}
                        placeholder="Country" data-testid="ddn-to-country" />
                    </div>
                  </div>
                </div>
              </div>

              {/* Dispatch From Address */}
              <div className="border-t pt-4">
                <h3 className="text-sm font-bold text-slate-700 mb-3 flex items-center gap-2">
                  <Send className="w-4 h-4 text-emerald-600 rotate-180" /> Dispatch From (Sender Address)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <FieldLabel label="Enterprise Name" />
                    <Input value={ddnDispatchFrom.enterprise_name} onChange={e => setDdnDispatchFrom(prev => ({ ...prev, enterprise_name: e.target.value }))}
                      placeholder="Organisation name" data-testid="ddn-from-enterprise" />
                  </div>
                  <div>
                    <FieldLabel label="Street" />
                    <Input value={ddnDispatchFrom.street} onChange={e => setDdnDispatchFrom(prev => ({ ...prev, street: e.target.value }))}
                      placeholder="Street address" data-testid="ddn-from-street" />
                  </div>
                  <div>
                    <FieldLabel label="City" />
                    <Input value={ddnDispatchFrom.city} onChange={e => setDdnDispatchFrom(prev => ({ ...prev, city: e.target.value }))}
                      placeholder="City" data-testid="ddn-from-city" />
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <FieldLabel label="Postal Code" />
                      <Input value={ddnDispatchFrom.postal_zip_code} onChange={e => setDdnDispatchFrom(prev => ({ ...prev, postal_zip_code: e.target.value }))}
                        placeholder="Postal/Zip" data-testid="ddn-from-postal" />
                    </div>
                    <div>
                      <FieldLabel label="Country" />
                      <Input value={ddnDispatchFrom.country} onChange={e => setDdnDispatchFrom(prev => ({ ...prev, country: e.target.value }))}
                        placeholder="Country" data-testid="ddn-from-country" />
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action buttons */}
          <div className="mt-4 space-y-3">
            <Button onClick={saveDdnInfo} disabled={isLoading}
              className="w-full bg-slate-200 hover:bg-slate-300 text-slate-700 font-medium py-6" variant="secondary"
              data-testid="save-ddn-info-btn">
              {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {savedDdn ? 'Update DDN Information' : 'Save DDN Information'}
            </Button>
            <Button onClick={openDdnBuilder} disabled={!savedDdn}
              className="w-full bg-violet-600 hover:bg-violet-700 text-white font-medium py-6"
              data-testid="build-ddn-btn">
              <PackageCheck className="w-4 h-4 mr-2" /> Build DDN
            </Button>
            <Button onClick={openDdnHistory} variant="outline" className="w-full py-5" data-testid="ddn-history-btn">
              <History className="w-4 h-4 mr-2" /> DDN Build History
            </Button>
          </div>
        </TabsContent>

        {/* ============ TAB: View List ============ */}
        <TabsContent value="list" className="mt-4">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
            <h2 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
              View List ({selectedDml?.entries?.length || 0})
            </h2>
            <div className="flex gap-2 flex-wrap">
              {!dirHandle ? (
                <Button variant="outline" size="sm" onClick={linkWorkingDirectory} data-testid="link-dir-btn">
                  <FolderOpen className="w-4 h-4 mr-2" /> Link Working Directory
                </Button>
              ) : (
                <>
                  <Badge variant="secondary" className="flex items-center gap-1.5 px-3 py-1.5 text-xs">
                    <FolderSync className="w-3.5 h-3.5 text-violet-600" />
                    <span className="font-mono">{dirName}</span>
                    <span className="text-slate-400">({dirFiles.length} XML files)</span>
                  </Badge>
                  <Button variant="outline" size="sm" onClick={refreshDirectory} disabled={scanningDir} data-testid="refresh-dir-btn">
                    {scanningDir ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
                  </Button>
                  <Button variant="outline" size="sm" onClick={unlinkDirectory} className="text-red-600 border-red-200 hover:bg-red-50" data-testid="unlink-dir-btn">
                    <XIcon className="w-3.5 h-3.5" />
                  </Button>
                </>
              )}
              {selectedDml && selectedDml.entries?.length > 0 && (
                <>
                  <Button variant="outline" size="sm" onClick={exportDml} data-testid="export-dml-list-btn">
                    <Download className="w-4 h-4 mr-2" /> Export DML
                  </Button>
                  <Button variant="outline" size="sm" onClick={exportDmShells} data-testid="export-shells-btn">
                    <FileText className="w-4 h-4 mr-2" /> Export DM Shells
                  </Button>
                </>
              )}
            </div>
          </div>

          {/* Working directory status summary */}
          {dirHandle && selectedDml?.entries?.length > 0 && (
            <div className="flex items-center gap-3 mb-3 text-sm" data-testid="dir-status-summary">
              <Badge className="bg-emerald-100 text-emerald-700">
                <Check className="w-3 h-3 mr-1" /> {viewListData.entriesWithStatus.filter(e => e.fileMatch).length} matched
              </Badge>
              <Badge className="bg-amber-100 text-amber-700">
                {viewListData.entriesWithStatus.filter(e => !e.fileMatch).length} missing in directory
              </Badge>
              {viewListData.unmatchedFiles.length > 0 && (
                <Badge variant="outline" className="text-slate-500">
                  {viewListData.unmatchedFiles.length} extra files in directory
                </Badge>
              )}
            </div>
          )}

          <Card className="border-slate-200">
            <CardContent className="p-0">
              {!selectedDml || (selectedDml.entries?.length === 0 && dirFiles.length === 0) ? (
                <div className="text-center py-12 px-6">
                  <List className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <p className="text-slate-500">No modules added yet</p>
                  <p className="text-sm text-slate-400 mt-1">Use the &quot;Add Data Module&quot; tab to add entries, or link a working directory to view XML files</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full" data-testid="view-list-table">
                    <thead>
                      <tr className="bg-slate-100 border-b border-slate-200">
                        {dirHandle && <th className="w-10 px-3 py-3 text-center text-xs font-semibold text-slate-600 uppercase tracking-wider">File</th>}
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider px-4 py-3">Data Module Code</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider px-4 py-3">Data Module Title</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider px-4 py-3">Schema</th>
                        {dirHandle && <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wider px-4 py-3">File Name</th>}
                        <th className="w-20 px-2 py-3"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {viewListData.entriesWithStatus.map((entry) => (
                        <tr key={entry.index} className={`border-b border-slate-100 hover:bg-slate-50 transition-colors group ${dirHandle && !entry.fileMatch ? 'bg-amber-50/40' : ''}`} data-testid={`entry-${entry.index}`}>
                          {dirHandle && (
                            <td className="px-3 py-3 text-center">
                              {entry.fileMatch ? (
                                <Check className="w-4 h-4 text-emerald-600 mx-auto" />
                              ) : (
                                <XIcon className="w-4 h-4 text-amber-500 mx-auto" />
                              )}
                            </td>
                          )}
                          <td className="px-4 py-3">
                            <code className="text-sm font-mono text-violet-700">{entry.display_code}</code>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-700">{entry.title}</td>
                          <td className="px-4 py-3 text-sm text-slate-600">{entry.schema || '-'}</td>
                          {dirHandle && (
                            <td className="px-4 py-3 text-xs font-mono text-slate-500">
                              {entry.fileMatch ? entry.fileMatch.name : <span className="text-amber-500 italic">Not found</span>}
                            </td>
                          )}
                          <td className="px-2 py-3">
                            <div className="flex items-center gap-1">
                              <button onClick={() => editEntry(entry.index)}
                                className="p-1.5 text-slate-400 hover:text-violet-600 transition-colors rounded hover:bg-violet-50"
                                data-testid={`edit-entry-${entry.index}`} title="Edit">
                                <Pencil className="w-4 h-4" />
                              </button>
                              <button onClick={() => removeEntry(entry.index)}
                                className="p-1.5 text-slate-400 hover:text-red-500 transition-colors rounded hover:bg-red-50"
                                data-testid={`remove-entry-${entry.index}`} title="Delete">
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                      {/* Show unmatched files from working directory */}
                      {dirHandle && viewListData.unmatchedFiles.length > 0 && (
                        <>
                          <tr className="bg-slate-50 border-b border-slate-200">
                            <td colSpan={dirHandle ? 6 : 4} className="px-4 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wider">
                              Extra files in working directory (not in DML)
                            </td>
                          </tr>
                          {viewListData.unmatchedFiles.map((file, i) => (
                            <tr key={`extra-${i}`} className="border-b border-slate-100 bg-slate-50/30 text-slate-400" data-testid={`extra-file-${i}`}>
                              <td className="px-3 py-2.5 text-center">
                                <FileText className="w-4 h-4 text-slate-300 mx-auto" />
                              </td>
                              <td className="px-4 py-2.5" colSpan={2}>
                                <code className="text-xs font-mono">{file.name}</code>
                              </td>
                              <td className="px-4 py-2.5 text-xs">-</td>
                              <td className="px-4 py-2.5 text-xs font-mono">{(file.size / 1024).toFixed(1)} KB</td>
                              <td className="px-2 py-2.5"></td>
                            </tr>
                          ))}
                        </>
                      )}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Export Dialog */}
      <Dialog open={showExportDialog} onOpenChange={setShowExportDialog}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>DML XML Export</DialogTitle>
            <DialogDescription>S1000D compliant XML output</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={downloadXml} className="bg-violet-600 hover:bg-violet-700" data-testid="download-xml-btn">
                <Download className="w-4 h-4 mr-2" /> Download XML
              </Button>
              <Button variant="outline" onClick={copyToClipboard} data-testid="copy-xml-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy to Clipboard
              </Button>
            </div>
            <ScrollArea className="h-[400px] border rounded-lg">
              <pre className="p-4 text-xs font-mono text-slate-700 whitespace-pre-wrap">{exportContent}</pre>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Duplicate DMC Alert */}
      <AlertDialog open={showDuplicateAlert} onOpenChange={setShowDuplicateAlert}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Duplicate Data Module Code</AlertDialogTitle>
            <AlertDialogDescription>
              This Data Module Code already exists in the current DML. Duplicate codes are not permitted. The entry will not be saved.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogAction onClick={() => setShowDuplicateAlert(false)} data-testid="duplicate-alert-ok">
              OK
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* DDN Builder Dialog */}
      <Dialog open={showDdnBuilder} onOpenChange={setShowDdnBuilder}>
        <DialogContent className="max-w-4xl max-h-[85vh]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>Build Data Dispatch Note</DialogTitle>
            <DialogDescription>Select the Data Modules, Publication Modules, and files to include in the delivery list. ICN files from the working directory are automatically included.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {/* Select All / Deselect All */}
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-slate-700">
                {ddnDeliveryFiles.length} file{ddnDeliveryFiles.length !== 1 ? 's' : ''} selected
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" data-testid="ddn-select-all"
                  onClick={() => {
                    const all = [
                      ...ddnCandidateFiles.candidates.map(c => c.filename),
                      ...ddnCandidateFiles.icnFiles,
                    ];
                    setDdnDeliveryFiles([...new Set(all)]);
                  }}>
                  Select All
                </Button>
                <Button variant="outline" size="sm" data-testid="ddn-deselect-all"
                  onClick={() => setDdnDeliveryFiles([])}>
                  Deselect All
                </Button>
              </div>
            </div>

            {/* DMs / PMs / Files list */}
            <ScrollArea className="h-[300px] border rounded-lg">
              <div className="p-3 space-y-1">
                {ddnCandidateFiles.candidates.length === 0 && ddnCandidateFiles.icnFiles.length === 0 && (
                  <p className="text-sm text-slate-400 text-center py-8">No entries in the DML or working directory. Add Data Modules first.</p>
                )}
                {ddnCandidateFiles.candidates.map((c, i) => (
                  <label key={`cand-${i}`} className="flex items-center gap-3 p-2 rounded hover:bg-slate-50 cursor-pointer" data-testid={`ddn-file-${i}`}>
                    <input type="checkbox" className="rounded border-slate-300 text-violet-600 focus:ring-violet-500"
                      checked={ddnDeliveryFiles.includes(c.filename)}
                      onChange={async (e) => {
                        if (e.target.checked) {
                          setDdnDeliveryFiles(prev => [...new Set([...prev, c.filename])]);
                          // auto-tick ICNs referenced in this DM's entity block / content
                          try {
                            const refIcns = await collectReferencedIcns([c.filename]);
                            if (refIcns.length > 0) {
                              setDdnDeliveryFiles(prev => {
                                const added = refIcns.filter(n => !prev.includes(n));
                                if (added.length > 0) {
                                  toast.info(`${added.length} referenced ICN${added.length !== 1 ? 's' : ''} auto-selected for ${c.filename}`);
                                }
                                return [...new Set([...prev, ...refIcns])];
                              });
                            }
                          } catch { /* directory unavailable */ }
                        } else {
                          setDdnDeliveryFiles(prev => prev.filter(f => f !== c.filename));
                        }
                      }} />
                    <Badge variant="outline" className={`text-[10px] px-1.5 ${c.type === 'DM' ? 'border-blue-300 text-blue-700' : c.type === 'PM' ? 'border-violet-300 text-violet-700' : 'border-slate-300 text-slate-600'}`}>{c.type}</Badge>
                    <code className="text-xs font-mono text-slate-700 flex-1 truncate">{c.filename}</code>
                    {c.title && <span className="text-xs text-slate-400 truncate max-w-[200px]">{c.title}</span>}
                  </label>
                ))}

                {/* ICN files section */}
                {ddnCandidateFiles.icnFiles.length > 0 && (
                  <>
                    <div className="border-t mt-3 pt-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">
                          ICN Files ({ddnCandidateFiles.icnFiles.length}) — referenced ICNs are pre-selected; untick any you don't want dispatched
                        </span>
                        <Button variant="ghost" size="sm" className="text-xs h-6"
                          onClick={() => {
                            const allIcns = ddnCandidateFiles.icnFiles;
                            const allIncluded = allIcns.every(f => ddnDeliveryFiles.includes(f));
                            if (allIncluded) {
                              setDdnDeliveryFiles(prev => prev.filter(f => !allIcns.includes(f)));
                            } else {
                              setDdnDeliveryFiles(prev => [...new Set([...prev, ...allIcns])]);
                            }
                          }}
                          data-testid="ddn-toggle-icns">
                          {ddnCandidateFiles.icnFiles.every(f => ddnDeliveryFiles.includes(f)) ? 'Remove All ICNs' : 'Include All ICNs'}
                        </Button>
                      </div>
                      {ddnCandidateFiles.icnFiles.map((icn, i) => (
                        <label key={`icn-${i}`} className="flex items-center gap-3 p-1.5 rounded hover:bg-amber-50 cursor-pointer">
                          <input type="checkbox" className="rounded border-slate-300 text-amber-600 focus:ring-amber-500"
                            checked={ddnDeliveryFiles.includes(icn)}
                            onChange={(e) => {
                              if (e.target.checked) setDdnDeliveryFiles(prev => [...prev, icn]);
                              else setDdnDeliveryFiles(prev => prev.filter(f => f !== icn));
                            }} />
                          <Badge variant="outline" className="text-[10px] px-1.5 border-amber-300 text-amber-700">ICN</Badge>
                          <code className="text-xs font-mono text-slate-600">{icn}</code>
                        </label>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </ScrollArea>

            {/* Build button */}
            <Button className="w-full bg-violet-600 hover:bg-violet-700 py-5" onClick={buildDdnXml}
              disabled={ddnBuilding || ddnDeliveryFiles.length === 0} data-testid="ddn-generate-btn">
              {ddnBuilding ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <PackageCheck className="w-4 h-4 mr-2" />}
              Generate DDN XML ({ddnDeliveryFiles.length} files)
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* DDN Export Dialog */}
      <Dialog open={showDdnExport} onOpenChange={setShowDdnExport}>
        <DialogContent className="max-w-3xl max-h-[80vh]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>DDN XML Export</DialogTitle>
            <DialogDescription>S1000D compliant Data Dispatch Note</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex gap-2">
              <Button onClick={downloadDdnXml} className="bg-violet-600 hover:bg-violet-700" data-testid="download-ddn-xml-btn">
                <Download className="w-4 h-4 mr-2" /> Download XML
              </Button>
              <Button onClick={downloadDdnZip} disabled={ddnZipBuilding} className="bg-emerald-600 hover:bg-emerald-700" data-testid="download-ddn-zip-btn">
                {ddnZipBuilding ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <PackageCheck className="w-4 h-4 mr-2" />}
                Download DDN Package (ZIP)
              </Button>
              <Button variant="outline" onClick={copyDdnToClipboard} data-testid="copy-ddn-xml-btn">
                <Copy className="w-4 h-4 mr-2" /> Copy to Clipboard
              </Button>
            </div>
            <div className="text-xs text-slate-500">
              Filename: <code className="font-mono bg-slate-100 px-1.5 py-0.5 rounded">{ddnExportFilename}</code>
            </div>
            <ScrollArea className="h-[400px] border rounded-lg">
              <pre className="p-4 text-xs font-mono text-slate-700 whitespace-pre-wrap">{ddnExportContent}</pre>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* DDN Build History Dialog */}
      <Dialog open={showDdnHistory} onOpenChange={setShowDdnHistory}>
        <DialogContent className="max-w-2xl" data-testid="ddn-history-dialog">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Outfit, sans-serif' }}>DDN Build History</DialogTitle>
            <DialogDescription>Previously built Data Dispatch Notes for this project. Re-open any build to download its XML or rebuild the ZIP package.</DialogDescription>
          </DialogHeader>
          {ddnHistoryLoading ? (
            <div className="py-8 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-violet-500" /></div>
          ) : ddnHistory.length === 0 ? (
            <p className="text-sm text-slate-500 text-center py-8">No DDN builds yet. Use Build DDN to create your first dispatch.</p>
          ) : (
            <ScrollArea className="max-h-[420px]">
              <div className="space-y-2 pr-2">
                {ddnHistory.map(b => (
                  <div key={b.id} className="flex items-center justify-between gap-3 border border-slate-200 rounded-lg px-3 py-2 hover:bg-slate-50" data-testid={`ddn-build-${b.id}`}>
                    <div className="min-w-0">
                      <code className="text-xs font-mono text-violet-700 block truncate">{b.filename}</code>
                      <p className="text-[11px] text-slate-500">
                        {new Date(b.built_at).toLocaleString()} · {b.built_by} · {b.file_count} file{b.file_count !== 1 ? 's' : ''}{b.receiver_ident ? ` · to ${b.receiver_ident}` : ''}
                      </p>
                    </div>
                    <Button variant="outline" size="sm" onClick={() => reopenDdnBuild(b)} data-testid={`ddn-build-open-${b.id}`}>
                      <Download className="w-3.5 h-3.5 mr-1.5" /> Open
                    </Button>
                  </div>
                ))}
              </div>
            </ScrollArea>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DMLBuilderPage;
